import type {
  CourtPriceBand,
  CourtPriceBandSaveInput,
  CourtPriceBandView,
  CourtPriceDateItem,
  CourtPriceDateSaveInput,
  CourtPriceDayType,
} from './priceTypes'

/**
 * 코트 요금 폼 상태 ↔ 저장 바디 매핑 + 사전 검증 + **겹치지 않는 시각만 남기는 선택지 계산**
 * (설계 §API/인터페이스 변경, D5·D6).
 *
 * 🚨 `if (...)` 판정을 컴포넌트에 흩지 않는다 — `courtViewModel.ts`·`operatingHourViewModel.ts` 규칙 승계.
 * 🚨 겹침의 **최종 판정자는 서버**다. 여기 있는 것은 ⓐ 애초에 못 고르게 하는 필터와 ⓑ 서버 검증표를
 *    미러링한 사전 검증 두 겹이며, 서버 400 문장은 화면이 그대로 보여 준다(설계 D5·D9).
 * 🚨 시작·종료 select 의 시각 후보는 **코트의 예약 단위(`reservationUnitMinutes`) 격자**를 따른다
 *    (`#876` — 예약 단위와 무관하게 30분 고정이던 것을 코트별 단위에 맞춘다). 격자 기준은 항상 자정(00:00)
 *    이다(`#876` 자율 결정 A1).
 */

/** 하루의 끝. `"24:00"` = 1440분(`23:59` 이 아니다 — `#852` §2-2). */
const DAY_END_MINUTES = 1440

/** "HH:mm" → 자정으로부터의 분. 형식이 깨지면 `NaN`. */
export function toMinutes(time: string): number {
  if (!/^\d{2}:\d{2}$/.test(time)) return Number.NaN
  const [hoursText, minutesText] = time.split(':')
  return Number(hoursText) * 60 + Number(minutesText)
}

/** 분 → "HH:mm". 1440 은 `"24:00"`. */
export function fromMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return `${String(hours).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
}

/**
 * 예약 단위 격자(`#876` 요구 1) — `"00:00"` 부터 `unitMinutes` 간격으로 하루 끝(`"24:00"`)까지.
 * 자정 기준 고정(코트의 실제 운영 시작 시각과 무관 — 자율 결정 A1). `unitMinutes=30` 이면 기존
 * `TIME_OF_DAY_OPTIONS`(00:00~24:00, 30분 간격 49개)와 결과가 완전히 같다.
 * `unitMinutes` 가 1440(하루)의 약수가 아니어도 마지막에 `"24:00"` 을 보정해 넣어 항상 자정 마감을 고를 수
 * 있게 한다.
 */
export function timeOptionsForUnit(unitMinutes: number): string[] {
  if (!Number.isFinite(unitMinutes) || unitMinutes <= 0) unitMinutes = 30

  const options: string[] = []
  for (let minutes = 0; minutes <= DAY_END_MINUTES; minutes += unitMinutes) {
    options.push(fromMinutes(minutes))
  }
  if (options[options.length - 1] !== fromMinutes(DAY_END_MINUTES)) {
    options.push(fromMinutes(DAY_END_MINUTES))
  }
  return options
}

/** 숫자 입력은 빈 문자열이 유효한 중간 상태라 텍스트로 다룬다(`courtViewModel.ts` 규칙 승계). */
export interface PriceBandRowFormState {
  startTime: string
  endTime: string
  hourlyFee: string
}

export interface PriceDayTypeFormState {
  dayType: CourtPriceDayType
  dayTypeLabel: string
  baseHourlyFee: string
  rows: PriceBandRowFormState[]
}

export interface PriceBandFormState {
  /** 🚨 서버 응답과 같은 2행(WEEKDAY → WEEKEND)을 그대로 유지한다. */
  dayTypes: PriceDayTypeFormState[]
}

export function toPriceBandFormState(view: CourtPriceBandView): PriceBandFormState {
  return {
    dayTypes: view.dayTypes.map((group) => ({
      dayType: group.dayType,
      dayTypeLabel: group.dayTypeLabel,
      baseHourlyFee: String(group.baseHourlyFee),
      rows: group.bands.map((band) => ({
        startTime: band.startTime,
        endTime: band.endTime,
        hourlyFee: String(band.hourlyFee),
      })),
    })),
  }
}

/** 폼 → `PUT …/price-bands` 바디. 두 유형의 구간이 **한 배열로 평탄화**되며 `dayType` 이 붙는다. */
export function toPriceBandSaveInput(form: PriceBandFormState): CourtPriceBandSaveInput {
  const baseOf = (dayType: CourtPriceDayType) =>
    Number(form.dayTypes.find((group) => group.dayType === dayType)?.baseHourlyFee ?? 0)

  return {
    weekdayHourlyFee: baseOf('WEEKDAY'),
    weekendHourlyFee: baseOf('WEEKEND'),
    bands: form.dayTypes.flatMap((group) =>
      group.rows.map((row) => ({
        dayType: group.dayType,
        startTime: row.startTime,
        endTime: row.endTime,
        hourlyFee: Number(row.hourlyFee),
      }))
    ),
  }
}

export function toPriceDateRows(item: CourtPriceDateItem | undefined): PriceBandRowFormState[] {
  return (item?.bands ?? []).map((band) => ({
    startTime: band.startTime,
    endTime: band.endTime,
    hourlyFee: String(band.hourlyFee),
  }))
}

/** 폼 → `PUT …/price-dates/{date}` 바디. 🚨 빈 배열은 "그날 규칙 삭제" 의도 그대로 전달된다. */
export function toPriceDateSaveInput(
  rows: PriceBandRowFormState[],
  label: string
): CourtPriceDateSaveInput {
  const trimmed = label.trim()
  return {
    label: trimmed.length > 0 ? trimmed : null,
    bands: rows.map((row) => ({
      startTime: row.startTime,
      endTime: row.endTime,
      hourlyFee: Number(row.hourlyFee),
    })),
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 요구 (3) — 겹치는 시각은 애초에 선택지에 없다
// ─────────────────────────────────────────────────────────────────────────────

interface MinuteRange {
  start: number
  end: number
}

/**
 * 편집 중인 행을 **제외한** 나머지 구간(분 단위). 자기 자신을 빼지 않으면 자기 시각을 되고를 수 없다.
 * 아직 다 채우지 않았거나 형식이 깨진 행은 판정에서 빠진다(중간 상태를 오류로 취급하지 않는다).
 */
function otherRangesOf(rows: PriceBandRowFormState[], editingIndex: number): MinuteRange[] {
  return rows
    .filter((_, index) => index !== editingIndex)
    .map((row) => ({ start: toMinutes(row.startTime), end: toMinutes(row.endTime) }))
    .filter((range) => Number.isFinite(range.start) && Number.isFinite(range.end) && range.end > range.start)
}

/**
 * 시작 시각 선택지.
 * 다른 구간의 **내부**(`start ≤ t < end`)인 시각을 지운다. 🔑 경계(`end`)는 남는다 —
 * `12:00~18:00` 다음에 `18:00` 으로 잇는 것은 겹침이 아니다(`#852` §2-2 "겹치지 않는 것").
 * `"24:00"` 은 시작이 될 수 없다(종료가 그보다 뒤일 수 없다).
 * 후보 시각 자체는 `unitMinutes` 격자(`timeOptionsForUnit`)로 좁힌다(`#876` 요구 1).
 */
export function startTimeOptions(
  rows: PriceBandRowFormState[],
  editingIndex: number,
  unitMinutes: number
): string[] {
  const others = otherRangesOf(rows, editingIndex)
  return timeOptionsForUnit(unitMinutes).filter((time) => {
    const minutes = toMinutes(time)
    if (minutes >= DAY_END_MINUTES) return false
    return !others.some((range) => minutes >= range.start && minutes < range.end)
  })
}

/**
 * 종료 시각 선택지 — `startTime < t ≤ 다음 구간의 시작`.
 * 뒤 구간을 물고 넘어가는 종료 시각이 **목록에 아예 없다**. 시작이 비어 있으면 빈 배열(select 는 잠긴다).
 * 후보 시각 자체는 `unitMinutes` 격자로 좁힌다(`#876` 요구 1).
 */
export function endTimeOptions(
  rows: PriceBandRowFormState[],
  editingIndex: number,
  startTime: string,
  unitMinutes: number
): string[] {
  const start = toMinutes(startTime)
  if (!Number.isFinite(start)) return []

  const nextStart = otherRangesOf(rows, editingIndex)
    .map((range) => range.start)
    .filter((candidate) => candidate > start)
    .reduce((closest, candidate) => Math.min(closest, candidate), DAY_END_MINUTES)

  return timeOptionsForUnit(unitMinutes).filter((time) => {
    const minutes = toMinutes(time)
    return minutes > start && minutes <= nextStart
  })
}

/**
 * 아직 어떤 구간도 덮지 않은 시간대(하루 전체 00:00~24:00 기준, `#866` D8 과 같은 축).
 * 🚨 맞닿은 구간 사이에는 잔여가 **생기지 않는다**(길이 0 창을 만들지 않는다).
 * 사고로 겹친 구간이 섞여 있어도 음수 길이를 내지 않는다.
 */
export function remainingWindowsOf(
  rows: PriceBandRowFormState[]
): { startTime: string; endTime: string }[] {
  const sorted = otherRangesOf(rows, -1).sort((left, right) => left.start - right.start)

  const windows: { startTime: string; endTime: string }[] = []
  let cursor = 0
  for (const range of sorted) {
    if (range.start > cursor) {
      windows.push({ startTime: fromMinutes(cursor), endTime: fromMinutes(range.start) })
    }
    cursor = Math.max(cursor, range.end)
  }
  if (cursor < DAY_END_MINUTES) {
    windows.push({ startTime: fromMinutes(cursor), endTime: fromMinutes(DAY_END_MINUTES) })
  }
  return windows
}

/** 새 구간 행의 초기값 — **첫 잔여 구간**을 그대로 채운다. 잔여가 없으면 `null`(추가 버튼이 잠긴다). */
export function nextBandRow(rows: PriceBandRowFormState[]): PriceBandRowFormState | null {
  const [firstWindow] = remainingWindowsOf(rows)
  if (!firstWindow) return null
  return { startTime: firstWindow.startTime, endTime: firstWindow.endTime, hourlyFee: '' }
}

/**
 * 시작 시각 변경 시 종료 시각 보정 — 새 시작 이후의 유효 선택지에 기존 종료가 없으면 **가장 뒤 선택지**
 * (= 다음 구간 시작 또는 24:00)로 옮긴다. 잘못된 값이 남아 저장 버튼에서야 튕기는 것을 막는다.
 */
export function applyStartTimeChange(
  rows: PriceBandRowFormState[],
  index: number,
  startTime: string,
  unitMinutes: number
): PriceBandRowFormState[] {
  return rows.map((row, rowIndex) => {
    if (rowIndex !== index) return row
    const options = endTimeOptions(rows, index, startTime, unitMinutes)
    const endTime = options.includes(row.endTime) ? row.endTime : (options[options.length - 1] ?? '')
    return { ...row, startTime, endTime }
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// 사전 검증 — `#852` §2-2 검증표 미러링
// ─────────────────────────────────────────────────────────────────────────────

/** 행 오류는 `index` 문자열 키로 담는다(행이 삭제·추가돼도 키가 흔들리지 않게 저장 직전에만 만든다). */
export interface PriceBandRowsErrors {
  rowErrors: Partial<Record<string, string>>
  general?: string
}

export interface PriceBandFormErrors {
  /** `dayType` → 기본가 오류. */
  baseErrors: Partial<Record<string, string>>
  /** `dayType` → 그 유형의 행/전역 오류. */
  dayTypeErrors: Partial<Record<string, PriceBandRowsErrors>>
}

function validateNonNegativeIntegerFee(value: string): string | undefined {
  const trimmed = value.trim()
  if (trimmed.length === 0) return '요금을 입력해 주세요.'
  if (!/^\d+$/.test(trimmed)) return '요금은 0원 이상의 정수로 입력해 주세요.'
  return undefined
}

/**
 * 한 축(요일유형 1개 또는 날짜 1건)의 구간 목록 검증.
 * 겹침 문구는 서버 문장(`"평일 요금 구간이 겹칩니다. (09:00~13:00, 12:00~18:00)"`)과 같은 꼴로 만든다.
 * 🚨 구간 길이가 `unitMinutes` 의 배수가 아니면 거부한다(`#876` 요구 2) — 서버는 이 규칙을 모르는
 *    **클라이언트 전용 가드**다(설계 불변식 3).
 */
export function validateBandRows(
  rows: PriceBandRowFormState[],
  axisLabel: string,
  maxRows: number,
  unitMinutes: number
): PriceBandRowsErrors {
  const errors: PriceBandRowsErrors = { rowErrors: {} }

  if (rows.length > maxRows) {
    errors.general = `${axisLabel} 요금 구간은 최대 ${maxRows}개입니다. (입력=${rows.length}개)`
  }

  rows.forEach((row, index) => {
    const start = toMinutes(row.startTime)
    const end = toMinutes(row.endTime)

    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      errors.rowErrors[String(index)] = '시작·종료 시각을 선택해 주세요.'
      return
    }
    if (end <= start) {
      errors.rowErrors[String(index)] = '종료 시각은 시작 시각보다 뒤여야 합니다.'
      return
    }
    if ((end - start) % unitMinutes !== 0) {
      errors.rowErrors[String(index)] =
        `구간 길이는 예약 단위(${unitMinutes}분)의 배수여야 합니다. (${row.startTime}~${row.endTime})`
      return
    }
    const feeError = validateNonNegativeIntegerFee(row.hourlyFee)
    if (feeError) errors.rowErrors[String(index)] = feeError
  })

  // 겹침은 행 단위 오류가 아니라 축 전역 오류다(어느 쪽이 "틀린" 행인지 정할 수 없다).
  const ranges = rows
    .map((row, index) => ({ index, start: toMinutes(row.startTime), end: toMinutes(row.endTime) }))
    .filter((range) => Number.isFinite(range.start) && Number.isFinite(range.end) && range.end > range.start)
    .sort((left, right) => left.start - right.start)

  for (let i = 1; i < ranges.length; i += 1) {
    const previous = ranges[i - 1]
    const current = ranges[i]
    if (previous && current && current.start < previous.end) {
      errors.general = `${axisLabel} 요금 구간이 겹칩니다. (${fromMinutes(previous.start)}~${fromMinutes(previous.end)}, ${fromMinutes(current.start)}~${fromMinutes(current.end)})`
      break
    }
  }

  return errors
}

export function validatePriceBandForm(
  form: PriceBandFormState,
  maxRowsPerDayType: number,
  unitMinutes: number
): PriceBandFormErrors {
  const errors: PriceBandFormErrors = { baseErrors: {}, dayTypeErrors: {} }

  for (const group of form.dayTypes) {
    const baseError = validateNonNegativeIntegerFee(group.baseHourlyFee)
    if (baseError) errors.baseErrors[group.dayType] = baseError

    const rowsErrors = validateBandRows(group.rows, group.dayTypeLabel, maxRowsPerDayType, unitMinutes)
    if (hasRowsErrors(rowsErrors)) errors.dayTypeErrors[group.dayType] = rowsErrors
  }

  return errors
}

export function hasRowsErrors(errors: PriceBandRowsErrors): boolean {
  return Boolean(errors.general) || Object.keys(errors.rowErrors).length > 0
}

export function hasPriceBandFormErrors(errors: PriceBandFormErrors): boolean {
  return (
    Object.keys(errors.baseErrors).length > 0 || Object.keys(errors.dayTypeErrors).length > 0
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// `#876` 요구 3 — 예약 단위가 바뀌어 기존에 저장된 구간이 새 단위와 어긋나는 경우 감지
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 서버가 준 원본 `bands`(수정 전) 중 길이가 `unitMinutes` 의 배수가 아닌 구간의 라벨만 뽑는다.
 * 운영시간 화면에서 예약 단위를 바꾼 뒤 이 화면에 들어오면, 저장 당시엔 유효했던 구간이 새 단위와
 * 어긋나 있을 수 있다 — 화면 진입 시 이 목록으로 경고 배너를 띄우고 재설정을 유도한다(자율 결정 A3).
 */
export function misalignedBandRanges(bands: CourtPriceBand[], unitMinutes: number): string[] {
  return bands
    .filter((band) => {
      const start = toMinutes(band.startTime)
      const end = toMinutes(band.endTime)
      return (
        Number.isFinite(start) && Number.isFinite(end) && end > start && (end - start) % unitMinutes !== 0
      )
    })
    .map((band) => band.timeRangeLabel ?? `${band.startTime}~${band.endTime}`)
}

// ─────────────────────────────────────────────────────────────────────────────
// 요구 (1) — 일괄 적용 결과 집계
// ─────────────────────────────────────────────────────────────────────────────

export interface BulkApplyTarget {
  courtId: number
  name: string
}

export interface BulkApplyResult extends BulkApplyTarget {
  ok: boolean
  /** 실패 사유(서버 문장 그대로). 성공이면 `undefined`. */
  message?: string
}

export interface BulkApplySummary {
  successCount: number
  failed: BulkApplyResult[]
  /** 요약 문장 — 화면이 조립하지 않고 이걸 그대로 쓴다. */
  message: string
}

/**
 * 팬아웃 결과 요약. 🚨 원자성이 없다는 사실을 문장으로 드러낸다 — 실패해도 성공분은 되돌리지 않는다
 * (설계 D2·R3).
 */
export function summarizeBulkApply(results: BulkApplyResult[]): BulkApplySummary {
  const failed = results.filter((result) => !result.ok)
  const successCount = results.length - failed.length

  if (failed.length === 0) {
    return { successCount, failed, message: `코트 ${successCount}면에 요금을 저장했습니다.` }
  }
  return {
    successCount,
    failed,
    message: `코트 ${successCount}면에 저장했고 ${failed.length}면은 실패했습니다. 실패한 코트만 다시 시도할 수 있어요.`,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 날짜 축 보조
// ─────────────────────────────────────────────────────────────────────────────

const KST_DATE_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/**
 * KST 달력 날짜("yyyy-MM-dd").
 * 🚨 기준은 **브라우저 시계가 아니라 응답 `baseTime`** 이다 — 서버가 과거 날짜를 KST 오늘 기준으로
 * 400 처리하므로(`#866` T8) 로컬 시간대로 판정하면 자정 근처에서 화면과 서버가 갈린다(설계 D7).
 */
export function toKstDate(baseTime: string | null | undefined): string {
  const source = baseTime ? new Date(baseTime) : new Date()
  const date = Number.isNaN(source.getTime()) ? new Date() : source
  const parts = Object.fromEntries(
    KST_DATE_FMT.formatToParts(date)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value])
  )
  return `${parts.year}-${parts.month}-${parts.day}`
}

/** `date` 로부터 `days` 일 뒤의 KST 달력 날짜. 조회 범위(`from`/`to`) 계산 전용. */
export function addDays(date: string, days: number): string {
  const parsed = new Date(`${date}T00:00:00Z`)
  parsed.setUTCDate(parsed.getUTCDate() + days)
  return parsed.toISOString().slice(0, 10)
}

/** 구간 요약 문구(날짜 목록 카드). 서버 `timeRangeLabel` 이 없을 때만 화면이 조립한다. */
export function toPriceDateSummary(item: CourtPriceDateItem): string {
  if (item.bands.length === 0) return '설정된 구간 없음'
  return item.bands
    .map((band) => `${band.timeRangeLabel ?? `${band.startTime}~${band.endTime}`} ${band.hourlyFee.toLocaleString('ko-KR')}원`)
    .join(' · ')
}
